import {
  EditOutlined,
  ReloadOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { Button, notification } from "antd";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";

import { useAuth, useLoc, useSchool, useSdk } from "@app/shared/lib/hooks";
import { useUser } from "@app/shared/lib/hooks/useUser";
import { Loc } from "@app/shared/ui/Loc";

type EmailConfirmationFormProps = {
  onEmailChange: () => void;
  canLogout: boolean;
};

export const EmailConfirmationForm: React.FC<EmailConfirmationFormProps> = ({
  onEmailChange,
  canLogout,
}) => {
  const [isSending, setSending] = useState(false);
  const loc = useLoc();
  const sdk = useSdk();
  const { user } = useUser();
  const school = useSchool();
  const [timeToResendInSec, setTimeToResendInSec] = useState(0);
  const [emailConfirmationSentDate, setEmailConfirmationSentDate] = useState(
    user?.emailConfirmationSentDate ?? new Date(0),
  );
  const { logout } = useAuth();

  useEffect(() => {
    const userEmailConfirmationSentDate = user?.emailConfirmationSentDate;
    if (userEmailConfirmationSentDate) {
      setEmailConfirmationSentDate(userEmailConfirmationSentDate);
    }
  }, [user?.emailConfirmationSentDate]);

  useEffect(() => {
    if (emailConfirmationSentDate) {
      const intervalId = window.setInterval(() => {
        const resendDate = moment(emailConfirmationSentDate).add(5, "minutes");
        setTimeToResendInSec(resendDate.diff(moment(), "seconds"));
      }, 1000);

      return () => window.clearInterval(intervalId);
    }
  }, [emailConfirmationSentDate]);

  const canResendConfirmation = useMemo(
    () => timeToResendInSec <= 0,
    [timeToResendInSec],
  );

  const timeToResend = useMemo(() => {
    const minutes = Math.trunc(timeToResendInSec / 60);
    const sec = timeToResendInSec - minutes * 60;

    return `${minutes}:${sec < 10 ? `0${sec}` : sec}`;
  }, [timeToResendInSec]);

  const resend = async () => {
    setSending(true);
    try {
      const result = await sdk.api.account.resendEmailConfirmation();
      if (result.success) {
        notification.info({
          message: loc.word("Confirmation email has been sent"),
        });
      }

      setEmailConfirmationSentDate(new Date());
    } finally {
      setSending(false);
    }
  };

  const footerBtns = (
    <div className="flex justify-between gap-10">
      {!school.profileSettings.blockEmailChange ? (
        <Button onClick={() => onEmailChange()} icon={<EditOutlined />}>
          <Loc word="Change email" />
        </Button>
      ) : null}
      <Button
        onClick={() => resend()}
        loading={isSending}
        type="primary"
        icon={<ReloadOutlined />}
        disabled={!canResendConfirmation}
      >
        <Loc word="Resend email confirmation" />
        {!canResendConfirmation && <span>&nbsp;{timeToResend}</span>}
      </Button>
    </div>
  );

  return (
    <>
      <Loc word="Confirmation mail instructions">
        <b>{user?.email}</b>
      </Loc>
      {canLogout && (
        <div className="mt-10">
          <Button danger icon={<LogoutOutlined />} onClick={logout}>
            {loc.word("Logout", { default: "Logout" })}
          </Button>
        </div>
      )}

      <footer>{footerBtns}</footer>
    </>
  );
};
